import { formatDate, useMutation } from '@betterment/js-runtime';
import { Text } from '@betterment/samba-react';
import { Box, Button, HR, ListRow, Modal } from '@betterment/style-closet-react';

import { DateFilter, toDateRange } from '../../../Shared/date-filter';
import { urlBuilder } from '../../../Shared/urlBuilder';
import { UnformattedTransactionsFilterParams } from '../../data';
import { useAccount } from '../AccountProvider';

export interface Props {
  filterParams: UnformattedTransactionsFilterParams;
  onClose: () => void;
  setShowExportSuccessBanner: (active: boolean) => void;
  setShowExportFailureBanner: (active: boolean) => void;
}

interface ExportParams {
  startOn: string | undefined;
  endOn: string | undefined;
  categories: string[] | null;
}

const getCategoryText = (categories: string[] | undefined) => {
  if (categories === undefined || categories?.length === 0) {
    return 'All';
  }
  if (categories.length === 1) {
    return categories[0];
  }
  return `${categories.length} categories`;
};

const TransactionExportModal: React.FC<React.PropsWithChildren<Props>> = ({
  filterParams,
  onClose,
  setShowExportSuccessBanner,
  setShowExportFailureBanner,
}) => {
  const { account } = useAccount();
  const [postCreateExport, { loading: isLoading }] = useMutation<{}, ExportParams>(
    urlBuilder.createDetailedTransactionExport(account.id)
  );

  const { dateFilter = DateFilter.PastMonth, from, to, categories = [] } = filterParams;
  const isCustomFilter = dateFilter === DateFilter.Custom;
  const dateString = isCustomFilter && from && to ? `${formatDate(from)} - ${formatDate(to)}` : dateFilter;

  const { startOn, endOn } = toDateRange(dateFilter as DateFilter, from, to) ?? {
    startOn: undefined,
    endOn: undefined,
  };

  const onSubmit = async () => {
    const { errors } = await postCreateExport({ startOn, endOn, categories });

    if (errors) {
      setShowExportFailureBanner(true);
    } else {
      setShowExportSuccessBanner(true);
    }
    onClose();
  };

  return (
    <Modal closeHandler={onClose} contentWidth={['100%', '480px']} variant="new">
      <Box py={[6, 3]} px={[6, 3]}>
        <Box px="6" mb="8" mt="6">
          <Text
            css={{
              fontSize: 'fontSize.300',
              fontWeight: 'bold',
            }}
          >
            Export transactions
          </Text>
          <Text
            as="p"
            css={{
              marginTop: 'space.200',
            }}
          >
            We'll email you a copy of your filtered transactions.
          </Text>
        </Box>
        <HR variant="list-row" />
        <ListRow variant="standard" leadingTitle="Date range" leadingSubtitle={dateString} />
        <HR variant="list-row" />
        <ListRow
          variant="standard"
          leadingTitle={categories?.length === 1 ? 'Category' : 'Categories'}
          leadingSubtitle={getCategoryText(categories)}
        />
        <HR variant="list-row" />
        <Button mt="9" mx="6" mb="6" variant="primary" disabled={isLoading} onClick={onSubmit}>
          Export
        </Button>
      </Box>
    </Modal>
  );
};

export default TransactionExportModal;
